import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Phone, Check, ArrowRight, Clock, Shield, ChevronRight } from "lucide-react";
import { companyInfo as mockCompany, services as mockServicesList, galleryImages } from "../data/mock";
import { useService, useServices } from "../hooks/useContent";
import PageHero from "../components/PageHero";

const serviceImages = {
  "servicii-complete-inmormantare-constanta": { src: galleryImages.churchInterior, alt: "Servicii complete de înmormântare în Constanța – Casa Funerară Sfântul Andrei" },
  "servicii-funerare-lumina": { src: galleryImages.sfAndreiCruce, alt: "Servicii funerare în Comuna Lumina – Casa Funerară Sfântul Andrei" },
  "organizare-inmormantare-completa": { src: galleryImages.waxCandle, alt: "Organizare înmormântare completă de la A la Z în Constanța" },
  "transport-funerar-local": { src: galleryImages.van, alt: "Transport funerar autorizat în Constanța – Casa Funerară Sfântul Andrei" },
  "transport-funerar-national": { src: galleryImages.mercedes, alt: "Transport funerar național din Constanța – vehicule autorizate" },
  "transport-funerar-international": { src: galleryImages.vanBranding, alt: "Transport funerar internațional și repatriere – Casa Funerară Sfântul Andrei" },
  "intocmire-acte-deces-documentatie": { src: galleryImages.sfAndreiBiserica, alt: "Întocmire acte de deces și documentație funerară completă în Constanța" },
  "imbalsamare-toaletare-decedat": { src: galleryImages.celestial, alt: "Pregătire și toaletare decedat cu demnitate – servicii funerare Constanța" },
  "servicii-incinerare": { src: galleryImages.sfAndreiVitraliu, alt: "Servicii de incinerare în Constanța – Casa Funerară Sfântul Andrei" },
  "aranjamente-florale-funerare": { src: galleryImages.flowers1, alt: "Coroane și aranjamente florale funerare în Constanța" },
  "parastas-pomeniri": { src: galleryImages.parastasPackages, alt: "Organizare parastas și pomeniri în Constanța – pachete complete" },
  "coliva-pachete-traditionale": { src: galleryImages.coliva, alt: "Colivă tradițională și pachete de pomenire – Casa Funerară Sfântul Andrei" },
  "consiliere-ajutor-inmormantare": { src: galleryImages.sfAndreiGrau, alt: "Consiliere ajutor de înmormântare 2026 – Casa Funerară Sfântul Andrei" },
  "servicii-funerare-non-stop": { src: galleryImages.opelVanFront, alt: "Servicii funerare NON-STOP 24/7 în Constanța – preluare imediată" },
};

const transportSlugs = ["transport-funerar-local", "transport-funerar-national", "transport-funerar-international", "servicii-funerare-non-stop"];

const fleetImages = [
  { src: galleryImages.van, alt: "Vehicul funerar autorizat – transport funerar în Constanța" },
  { src: galleryImages.mercedes, alt: "Mașină funerară Mercedes – transport național și internațional" },
  { src: galleryImages.vanBranding, alt: "Autovehicul funerar Casa Funerară Sfântul Andrei – transport autorizat" },
  { src: galleryImages.opelVanFront, alt: "Opel funerar autorizat – preluare imediată domiciliu, spital, IML" },
];
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const { data: service } = useService(slug);
  const { data: allServices } = useServices();

  const pageTitle = service
    ? (service.metaTitle || service.title + " – Casa Funerară Sfântul Andrei")
    : "Serviciu – Casa Funerară Sfântul Andrei";
  const pageDescription = service
    ? (service.metaDescription || service.intro.substring(0, 160))
    : "";
  const canonicalUrl = "https://funeraresfantulandrei.ro/servicii/" + slug;

  useEffect(() => { document.title = pageTitle; }, [pageTitle]);

  if (!service) {
    return <Navigate to="/servicii" replace />;
  }

  const relatedServices = allServices.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:url" content={canonicalUrl} />
      <PageHero
        image={serviceImages[service.slug]?.src || galleryImages.heroBackground}
        title={service.title}
        breadcrumbs={[
          { label: "Acasă", to: "/" },
          { label: "Servicii", to: "/servicii" },
          { label: service.shortTitle },
        ]}
      />
      {/* CTA bar below hero */}
      <div className="bg-[#0f0f0f] py-4">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 flex items-center gap-6">
          <a
            href={`tel:${mockCompany.phone}`}
            className="flex items-center gap-2 bg-white text-[#0f0f0f] px-6 py-2.5 text-sm font-semibold no-underline hover:bg-white/90 transition-colors"
            style={{ borderRadius: "var(--radius-pill)" }}
            data-testid="service-call-btn"
          >
            <Phone className="w-4 h-4" />
            Sună Acum
          </a>
          <div className="flex items-center gap-2 text-sm text-white/60">
            <Clock className="w-4 h-4" />
            Disponibil NON-STOP 24/7
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="section-spacing bg-white">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Introduction */}
              <div className="mb-10">
                <p className="text-[#666666] leading-[1.8] text-base">{service.intro}</p>
              </div>

              {/* Service Image */}
              {serviceImages[service.slug] && (
                <div className="mb-14 rounded-2xl overflow-hidden" style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>
                  <img
                    src={serviceImages[service.slug].src}
                    alt={serviceImages[service.slug].alt}
                    className="w-full h-72 object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              {/* Fleet Gallery – only for transport services */}
              {transportSlugs.includes(service.slug) && (
                <div className="mb-14">
                  <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Flota Noastră de Transport
                  </h2>
                  <p className="text-sm text-[#666666] mb-8 leading-relaxed">
                    Dispunem de vehicule funerare autorizate DSP, la standarde europene, pregătite oricând pentru intervenție. Toate mașinile sunt climatizate și echipate corespunzător pentru transport demn și sigur.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    {fleetImages.map((img, i) => (
                      <div key={i} className="rounded-xl overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-44 object-cover hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* What's Included */}
              <div className="mb-14">
                <h2
                  className="text-2xl font-semibold mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Ce Include Acest Serviciu
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {service.includes.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-[#FAFAF8] border border-[#E8E4DC]">
                      <Check className="w-4 h-4 text-[#0f0f0f] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-[#666666]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="mb-14">
                <h2
                  className="text-2xl font-semibold mb-8"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  De Ce Să Alegeți Casa Funerară Sfântul Andrei
                </h2>
                <div className="space-y-4">
                  {service.whyChoose.map((item, i) => (
                    <div key={i} className="flex items-center gap-4 p-5 border border-[#E8E4DC] hover:border-[#cccccc] transition-colors">
                      <div className="w-8 h-8 flex items-center justify-center bg-[#0f0f0f] flex-shrink-0">
                        <Shield className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-sm text-[#666666] font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Availability */}
              <div className="mb-14 p-8 bg-[#FAFAF8] border border-[#E8E4DC]">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-[#0f0f0f]" />
                  <h3
                    className="text-xl font-semibold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Disponibilitate NON-STOP
                  </h3>
                </div>
                <p className="text-sm text-[#666666] leading-relaxed">
                  Acest serviciu este disponibil 24 de ore din 24, 7 zile din 7, inclusiv
                  în zilele de sărbătoare. Sunați-ne oricând la{" "}
                  <a href={`tel:${mockCompany.phone}`} className="text-[#0f0f0f] font-semibold no-underline hover:underline">
                    {mockCompany.phone}
                  </a>
                  {" "}și vă oferim sprijin imediat.
                </p>
              </div>

              {/* FAQ */}
              {service.faqs && service.faqs.length > 0 && (
                <div className="faq-section">
                  <h2
                    className="text-2xl font-semibold mb-8"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Întrebări Frecvente
                  </h2>
                  <Accordion type="single" collapsible className="space-y-3">
                    {service.faqs.map((faq, i) => (
                      <AccordionItem
                        key={i}
                        value={`faq-${i}`}
                        className="border border-[#E8E4DC] px-6 data-[state=open]:border-[#cccccc] transition-colors"
                      >
                        <AccordionTrigger className="text-sm font-semibold text-[#0f0f0f] hover:no-underline py-5">
                          {faq.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-[#666666] leading-relaxed pb-5">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-36 space-y-8">
                {/* CTA Card */}
                <div className="bg-[#0f0f0f] text-white p-8">
                  <h3
                    className="text-xl font-semibold mb-4 text-white"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    Sună pentru Sprijin Imediat
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-6">
                    Suntem disponibili NON-STOP. Nu ezitați să ne contactați pentru orice întrebare sau nevoie urgentă.
                  </p>
                  <a
                    href={`tel:${mockCompany.phone}`}
                    className="flex items-center justify-center gap-2 bg-white text-[#0f0f0f] py-3 text-sm font-semibold no-underline hover:bg-white/90 transition-colors w-full"
                    style={{ borderRadius: "var(--radius-pill)" }}
                  >
                    <Phone className="w-4 h-4" />
                    {mockCompany.phone}
                  </a>
                  <a
                    href={`tel:${mockCompany.phoneSecondary}`}
                    className="block text-center text-white/50 text-xs mt-3 no-underline hover:text-white/70 transition-colors"
                  >
                    Sau: {mockCompany.phoneSecondary}
                  </a>
                </div>

                {/* Related Services */}
                <div className="border border-[#E8E4DC] p-8">
                  <h4 className="text-sm font-semibold uppercase tracking-[2px] text-[#999999] mb-6">
                    Servicii Similare
                  </h4>
                  <div className="space-y-4">
                    {relatedServices.map((rs) => (
                      <Link
                        key={rs.slug}
                        to={`/servicii/${rs.slug}`}
                        className="flex items-center justify-between text-sm text-[#666666] no-underline hover:text-[#0f0f0f] transition-colors group py-2 border-b border-[#f0f0f0] last:border-0"
                      >
                        <span>{rs.shortTitle}</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[#ccc] group-hover:text-[#0f0f0f] group-hover:translate-x-0.5 transition-all" />
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/servicii"
                    className="mt-6 flex items-center gap-2 text-xs font-semibold text-[#0f0f0f] uppercase tracking-wider no-underline hover:gap-3 transition-all"
                  >
                    Toate Serviciile
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>

                {/* Locations */}
                <div className="bg-[#FAFAF8] p-8 border border-[#E8E4DC]">
                  <h4 className="text-sm font-semibold uppercase tracking-[2px] text-[#999999] mb-6">
                    Sediile Noastre
                  </h4>
                  {mockCompany.addresses.map((addr, i) => (
                    <div key={i} className="mb-4 last:mb-0">
                      <p className="text-sm font-semibold text-[#0f0f0f] mb-0.5">{addr.name}</p>
                      <p className="text-xs text-[#999999]">{addr.street}, {addr.city}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="cta-section">
        <div className="max-w-[1140px] mx-auto px-4 lg:px-8 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-6 text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Sună Acum pentru Sprijin Imediat
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Echipa noastră este pregătită să vă ajute, oricând aveți nevoie. Sunați cu încredere.
          </p>
          <a
            href={`tel:${mockCompany.phone}`}
            className="inline-flex items-center gap-3 bg-white text-[#0f0f0f] px-10 py-4 text-sm font-semibold tracking-wide no-underline hover:bg-white/90 transition-colors"
            style={{ borderRadius: "var(--radius-pill)" }}
          >
            <Phone className="w-4 h-4" />
            {mockCompany.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
